import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import Loader from "../common/Loader";
import {
  fetchCRMAccounts,
  filterAccountsBySearchTerm,
  registerAccountsLoadingHandler,
  searchAccountsByName,
} from "../../api/CRMAccountsData";

export default function SkillsAnalysisTraining() {
  const [accounts, setAccounts] = useState([]);
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unregister = registerAccountsLoadingHandler((isLoading) => {
      setIsLoadingAccounts(isLoading);
    });

    const loadAccounts = async () => {
      try {
        const initialAccounts = await fetchCRMAccounts();
        setAccounts(initialAccounts);
        setFilteredAccounts(initialAccounts);
      } catch (error) {
        console.error("Error loading accounts:", error);
      } finally {
        setLoading(false);
      }
    };

    loadAccounts();

    return () => {
      unregister();
      if (searchTimeout) clearTimeout(searchTimeout);
    };
  }, []);

  const debouncedSearch = useCallback(
    (term) => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }

      if (!term || term.length < 2) {
        setFilteredAccounts(accounts);
        return;
      }

      const timeoutId = setTimeout(async () => {
        setIsLoadingAccounts(true);
        try {
          const results = await searchAccountsByName(term);
          setFilteredAccounts(results);
        } catch (error) {
          console.error("Error searching accounts:", error);
          const filtered = filterAccountsBySearchTerm(accounts, term);
          setFilteredAccounts(filtered);
        }
      }, 500);

      setSearchTimeout(timeoutId);
    },
    [accounts, searchTimeout]
  );

  const handleSearchChange = (event) => {
    const term = event.target.value;
    setSearchTerm(term);
    debouncedSearch(term);
  };

  const handleAccountChange = (event, newValue) => {
    setSelectedAccount(newValue);

    // Store account ID in window object for global access
    if (newValue && newValue.id) {
      window.selectedAccountId = newValue.id;
      window.selectedAccountName = newValue.name || "";
      console.log(
        "Account selected globally:",
        window.selectedAccountId,
        window.selectedAccountName
      );
    } else {
      window.selectedAccountId = null;
      window.selectedAccountName = "";
    }
  };

  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
  };

  if (loading) {
    return <Loader message="Loading skills analysis & training module..." />;
  }

  return (
    <>
      <Typography variant="h6" fontWeight="bold" color="Dark">
        Track employee training records, certifications, skill matrices
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}>
        <Autocomplete
          sx={{
            "& .MuiInputBase-input": {
              color: "#000000",
            },
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "#d3d3d3",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#4F46E5",
              },
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: "#4F46E5",
            },
          }}
          id="account-search"
          options={filteredAccounts}
          getOptionLabel={(option) => option.name || ""}
          onChange={handleAccountChange}
          onInputChange={handleInputChange}
          inputValue={inputValue}
          value={selectedAccount}
          loading={isLoadingAccounts}
          fullWidth
          filterOptions={(options, state) => options}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search Customer Name"
              size="small"
              variant="outlined"
              onChange={handleSearchChange}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoadingAccounts ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Box>
    </>
  );
}
